import { useState } from "react";
import { Button } from "./Button";
import type {
  CollectionWithPois,
  CollectionVisibility,
} from "@poi-planner/shared";
import {
  collectionVisibilityToneConfig,
  permissionToneConfig,
} from "../theme/colors";

interface CollectionCardProps {
  collection: CollectionWithPois;
  onDelete: (id: string) => void;
  onEdit: (collection: CollectionWithPois) => void;
  onShare?: (collectionId: string) => void;
  onClick?: (collection: CollectionWithPois) => void;
}

function VisibilityIcon({ visibility }: { visibility: CollectionVisibility }) {
  switch (visibility) {
    case "private":
      return <span className="material-symbols-outlined text-sm">lock</span>;
    case "public":
      return <span className="material-symbols-outlined text-sm">public</span>;
    case "shared":
      return <span className="material-symbols-outlined text-sm">group</span>;
  }
}

function VisibilityBadge({ visibility }: { visibility: CollectionVisibility }) {
  const config = {
    private: { ...collectionVisibilityToneConfig.private, label: "Private" },
    public: { ...collectionVisibilityToneConfig.public, label: "Public" },
    shared: { ...collectionVisibilityToneConfig.shared, label: "Shared" },
  }[visibility];

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: config.bg, color: config.color }}
    >
      <VisibilityIcon visibility={visibility} />
      {config.label}
    </span>
  );
}

export function CollectionCard({
  collection,
  onDelete,
  onEdit,
  onShare,
  onClick,
}: CollectionCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const permissionTone =
    collection.permission === "editor"
      ? permissionToneConfig.editor
      : permissionToneConfig.viewer;

  const handleClick = () => {
    onClick?.(collection);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirm) {
      onDelete(collection.id);
    } else {
      setShowConfirm(true);
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(collection);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(collection.id);
  };

  return (
    <div
      onClick={handleClick}
      className="group relative bg-white border border-border rounded-2xl p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-slate-200/50 hover:border-slate-300 hover:-translate-y-0.5"
    >
      {/* Permission Badge */}
      {!collection.isOwner && (
        <div
          className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold"
          style={{
            background: permissionTone.bg,
            color: permissionTone.color,
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "14px" }}
          >
            {collection.permission === "editor" ? "edit" : "visibility"}
          </span>
          <span>
            {collection.permission === "editor" ? "Editor" : "Viewer"}
          </span>
        </div>
      )}

      {/* Action buttons */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        {collection.isOwner &&
          onShare &&
          collection.visibility !== "private" && (
            <Button
              onClick={handleShare}
              variant="icon"
              icon="share"
              className="reveal-on-card-hover"
              title="Share collection"
              aria-label="Share collection"
            />
          )}
        {collection.isOwner && (
          <Button
            onClick={handleEdit}
            variant="icon"
            icon="edit"
            className="reveal-on-card-hover"
            title="Edit collection"
            aria-label="Edit collection"
          />
        )}
        {collection.isOwner && (
          <Button
            onClick={handleDelete}
            variant={showConfirm ? "danger" : "icon"}
            icon={showConfirm ? "check" : "delete"}
            className={showConfirm ? "opacity-100" : "reveal-on-card-hover"}
            title={showConfirm ? "Click again to confirm" : "Delete collection"}
            aria-label={
              showConfirm ? "Confirm delete collection" : "Delete collection"
            }
          />
        )}
      </div>

      {/* Collection icon */}
      <div className="flex items-center justify-center size-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-200/50 mb-4">
        <span className="material-symbols-outlined text-2xl text-amber-600">
          folder_special
        </span>
      </div>

      {/* Collection name */}
      <h3 className="text-base font-bold text-primary mb-1 pr-8 line-clamp-2">
        {collection.name}
      </h3>

      {/* Description */}
      {collection.description && (
        <p className="text-sm text-secondary mb-3 line-clamp-2">
          {collection.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-base text-tertiary">
            location_on
          </span>
          <span className="text-xs font-semibold text-tertiary">
            {collection.poiIds.length}{" "}
            {collection.poiIds.length === 1 ? "POI" : "POIs"}
          </span>
        </div>
        <VisibilityBadge visibility={collection.visibility} />
      </div>
    </div>
  );
}
